const AUDIENCE_FIELDS = ['response_agent_username', 'agent_user_id', 'phone_number'];

export function readInterveneAudienceFilters(filters = []) {
  const agentRow = (filters || []).find((item) => item.field === 'response_agent_username');
  const agentIdRow = (filters || []).find((item) => item.field === 'agent_user_id');
  const customerRow = (filters || []).find((item) => item.field === 'phone_number');

  const toList = (row) => {
    if (!row) return [];
    if (Array.isArray(row.value)) return row.value.map(String).filter(Boolean);
    return row.value != null && String(row.value).trim() ? [String(row.value).trim()] : [];
  };

  const agents = toList(agentRow);
  const agentUserIds = toList(agentIdRow);
  const customers = toList(customerRow);

  const res = {
    agent: agents[0] || '',
    agentUserId: agentUserIds[0] || '',
    customer: customers[0] || '',
  };

  Object.defineProperty(res, 'agents', { value: agents, writable: true, configurable: true, enumerable: false });
  Object.defineProperty(res, 'agentUserIds', { value: agentUserIds, writable: true, configurable: true, enumerable: false });
  Object.defineProperty(res, 'customers', { value: customers, writable: true, configurable: true, enumerable: false });

  return res;
}

export function mergeInterveneAudienceFilters(filters = [], patch = {}) {
  const current = readInterveneAudienceFilters(filters);
  const rest = (filters || []).filter(
    (item) => !AUDIENCE_FIELDS.includes(item.field)
  );
  const next = [...rest];

  const normalizeList = (val) => {
    if (val === undefined) return null;
    if (Array.isArray(val)) return val.map(String).filter(Boolean);
    return val != null && String(val).trim() ? [String(val).trim()] : [];
  };

  const nextAgents = normalizeList(patch.agents) ?? normalizeList(patch.agent) ?? current.agents;
  const nextAgentIds = normalizeList(patch.agentUserIds) ?? normalizeList(patch.agentUserId) ?? current.agentUserIds;
  const nextCustomers = normalizeList(patch.customers) ?? normalizeList(patch.customer) ?? current.customers;

  if (nextAgents.length > 1) {
    next.push({ field: 'response_agent_username', operator: 'in', value: nextAgents });
  } else if (nextAgents.length === 1) {
    next.push({ field: 'response_agent_username', operator: 'eq', value: nextAgents[0] });
  }

  if (nextAgentIds.length > 1) {
    next.push({ field: 'agent_user_id', operator: 'in', value: nextAgentIds });
  } else if (nextAgentIds.length === 1) {
    next.push({ field: 'agent_user_id', operator: 'eq', value: nextAgentIds[0] });
  }

  if (nextCustomers.length > 1) {
    next.push({ field: 'phone_number', operator: 'in', value: nextCustomers });
  } else if (nextCustomers.length === 1) {
    next.push({ field: 'phone_number', operator: 'eq', value: nextCustomers[0] });
  }

  return next;
}

export function stripInvalidFilters(filters = []) {
  return (filters || []).filter((item) => {
    const field = String(item?.field || '').trim();
    const value = String(item?.value ?? '').trim();
    if (!field) return false;
    if (item?.operator === 'in') {
      const values = Array.isArray(item.value) ? item.value : [item.value];
      return values.some((entry) => String(entry ?? '').trim());
    }
    return Boolean(value);
  });
}

export function isDurationMetricId(metricId) {
  return [
    'avg_agent_response',
    'median_agent_response',
    'avg_customer_response',
    'avg_first_agent_response',
  ].includes(metricId);
}

export function parseDurationDisplay(value) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  const text = String(value || '').trim();
  const hourMatch = text.match(/^(\d+)h\s*(\d+)m\s*(\d+)s$/i);
  if (hourMatch) {
    return parseInt(hourMatch[1], 10) * 3600
      + parseInt(hourMatch[2], 10) * 60
      + parseInt(hourMatch[3], 10);
  }
  const match = text.match(/^(\d+)m\s*(\d+)s$/i);
  if (!match) {
    return null;
  }
  return parseInt(match[1], 10) * 60 + parseInt(match[2], 10);
}

export function formatDurationSeconds(seconds) {
  const total = Math.max(0, Math.round(Number(seconds) || 0));
  const hours = Math.floor(total / 3600);
  if (hours > 0) {
    const minutes = Math.floor((total % 3600) / 60);
    const remaining = total % 60;
    return `${hours}h ${minutes}m ${remaining}s`;
  }
  const minutes = Math.floor(total / 60);
  const remaining = total % 60;
  return `${minutes}m ${remaining}s`;
}

export function getAgentResponseBenchmark(seconds) {
  const num = Number(seconds);
  if (!Number.isFinite(num) || num <= 0) {
    return null;
  }
  if (num <= 60) {
    return {
      rating: 'Excellent',
      label: '🌟 Fast (< 1m)',
      color: '#00a884',
      bg: '#ecfdf5',
    };
  }
  if (num <= 180) {
    return {
      rating: 'Good',
      label: '👍 Good (1-3m)',
      color: '#0288d1',
      bg: '#f0f9ff',
    };
  }
  if (num <= 300) {
    return {
      rating: 'Average',
      label: '⏱️ Average (3-5m)',
      color: '#1976d2',
      bg: '#eff6ff',
    };
  }
  return {
    rating: 'Needs Attention',
    label: '⚠️ Needs Attention (> 5m)',
    color: '#d97706',
    bg: '#fffbeb',
  };
}
